import React, { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Database, Mail, CheckCircle2, AlertCircle, XCircle, Sliders, Sparkles } from 'lucide-react';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Slider } from '../components/ui/slider';
import { connectors, defaultWeights, AlgorithmWeights } from '../lib/data';
function ConnectorStatus({
  status
}: {
  status: string;
}) {
  if (status === 'connected') {
    return <div className="flex items-center gap-1.5 text-sm text-sentiment-positive" data-id="element-1144">
        <CheckCircle2 className="h-3.5 w-3.5" data-id="element-1145" />
        Connesso
      </div>;
  }
  if (status === 'error') {
    return <div className="flex items-center gap-1.5 text-sm text-destructive" data-id="element-1146">
        <AlertCircle className="h-3.5 w-3.5" data-id="element-1147" />
        Errore
      </div>;
  }
  return <div className="flex items-center gap-1.5 text-sm text-muted-foreground" data-id="element-1148">
      <XCircle className="h-3.5 w-3.5" data-id="element-1149" />
      Non connesso
    </div>;
}
function ConnectorsTab() {
  return <div data-id="element-1150">
      <div className="mb-8" data-id="element-1151">
        <h2 className="text-lg font-semibold mb-1" data-id="element-1152">Integrazioni automatiche</h2>
        <p className="text-sm text-muted-foreground" data-id="element-1153">
          Configura i servizi per la raccolta automatica delle rassegne stampa.
        </p>
      </div>

      <div className="divide-y divide-border border-y border-border" data-id="element-1154">
        {connectors.map(connector => {
        const Icon = connector.id === 'email' ? Mail : Database;
        return <div key={connector.id} className="flex items-center gap-4 py-5 md:py-6" data-id="element-1155">
              <div className="w-10 h-10 rounded-md bg-muted flex items-center justify-center flex-shrink-0" data-id="element-1156">
                <Icon className="h-4 w-4 text-muted-foreground" data-id="element-1157" />
              </div>

              <div className="flex-1 min-w-0" data-id="element-1158">
                <div className="flex items-center gap-3 mb-1" data-id="element-1159">
                  <h3 className="font-medium text-base" data-id="element-1160">{connector.name}</h3>
                  <ConnectorStatus status={connector.status} data-id="element-1161" />
                </div>
                <p className="text-sm text-muted-foreground line-clamp-1" data-id="element-1162">
                  {connector.status === 'connected' && connector.lastSync ? <>
                      Ultimo sync{' '}
                      {new Date(connector.lastSync).toLocaleString('it-IT', {
                  day: '2-digit',
                  month: 'short',
                  hour: '2-digit',
                  minute: '2-digit'
                })}
                      {' · '}
                      {connector.rassegneIngested} rassegne totali
                    </> : connector.description}
                </p>
              </div>

              <div className="flex-shrink-0" data-id="element-1163">
                {connector.status === 'disconnected' ? <Button size="sm" className="rounded-full" data-id="element-1164">
                    Connetti
                  </Button> : connector.status === 'error' ? <Button size="sm" className="rounded-full" data-id="element-1165">
                    Riprova
                  </Button> : <Button variant="outline" size="sm" className="rounded-full border-accent text-accent hover:bg-accent/10 hover:text-accent" data-id="element-1166">
                    Configura
                  </Button>}
              </div>
            </div>;
      })}
      </div>
    </div>;
}
function WeightRow({
  label,
  description,
  value,
  onChange,
  max = 3
}: {
  label: string;
  description?: string;
  value: number;
  onChange: (v: number) => void;
  max?: number;
}) {
  return <div className="grid grid-cols-1 md:grid-cols-[280px_1fr_50px] gap-2 md:gap-6 items-center py-4" data-id="element-1167">
      <div data-id="element-1168">
        <div className="text-sm font-medium" data-id="element-1169">{label}</div>
        {description && <div className="text-xs text-muted-foreground mt-0.5" data-id="element-1170">
            {description}
          </div>}
      </div>
      <Slider value={[value]} onValueChange={v => onChange(v[0])} max={max} step={0.1} data-id="element-1171" />
      <div className="text-sm font-medium text-right tabular-nums" style={{
      fontVariantNumeric: 'tabular-nums'
    }} data-id="element-1172">
        +{value.toFixed(1)}
      </div>
    </div>;
}
function AlgorithmTab() {
  const [weights, setWeights] = useState<AlgorithmWeights>(defaultWeights);
  const update = (key: keyof AlgorithmWeights, value: number) => {
    setWeights(w => ({
      ...w,
      [key]: value
    }));
  };
  const sections: Array<{
    title: string;
    description: string;
    items: Array<{
      key: keyof AlgorithmWeights;
      label: string;
      description?: string;
      max?: number;
    }>;
  }> = [{
    title: 'Tier testata',
    description: 'Peso assegnato in base alla rilevanza editoriale della testata.',
    items: [{
      key: 'tier1',
      label: 'Tier 1',
      description: 'Testate principali (Sole 24 Ore, Corriere…)',
      max: 3
    }, {
      key: 'tier2',
      label: 'Tier 2',
      description: 'Testate secondarie',
      max: 3
    }, {
      key: 'tier3',
      label: 'Tier 3',
      description: 'Testate minori',
      max: 3
    }, {
      key: 'tierSpecialized',
      label: 'Specializzata',
      description: 'Settoriali e di nicchia',
      max: 3
    }]
  }, {
    title: 'Posizionamento',
    description: "Bonus per elementi di visibilità nell'articolo.",
    items: [{
      key: 'positioningFrontPage',
      label: 'Richiamo in prima pagina',
      max: 3
    }, {
      key: 'positioningTitleMention',
      label: 'Cliente nel titolo',
      max: 3
    }, {
      key: 'positioningBold',
      label: 'Citazione in grassetto',
      max: 2
    }, {
      key: 'positioningPhoto',
      label: 'Foto presente',
      max: 2
    }]
  }, {
    title: 'Tipologia contenuto',
    description: "Peso in base alla profondità dell'articolo.",
    items: [{
      key: 'contentInterview',
      label: 'Intervista',
      max: 3
    }, {
      key: 'contentSignedCommentary',
      label: 'Commento firmato',
      max: 3
    }, {
      key: 'contentDedicatedArticle',
      label: 'Articolo dedicato',
      max: 3
    }, {
      key: 'contentComment',
      label: 'Commento',
      max: 2
    }, {
      key: 'contentMention',
      label: 'Citazione',
      max: 2
    }]
  }, {
    title: 'Sentiment',
    description: "Modifica il punteggio in base al tono dell'articolo.",
    items: [{
      key: 'sentimentPositive',
      label: 'Sentiment positivo',
      max: 2
    }, {
      key: 'sentimentNeutral',
      label: 'Sentiment neutro',
      max: 2
    }, {
      key: 'sentimentNegative',
      label: 'Sentiment negativo',
      max: 2
    }]
  }, {
    title: 'Altro',
    description: '',
    items: [{
      key: 'authoritativeSignature',
      label: 'Firma autorevole',
      description: 'Bonus per articoli firmati da giornalisti autorevoli',
      max: 2
    }]
  }];
  return <div data-id="element-1173">
      <div className="mb-8 flex flex-col md:flex-row md:items-start md:justify-between gap-3" data-id="element-1174">
        <div data-id="element-1175">
          <h2 className="text-lg font-semibold mb-1" data-id="element-1176">
            Pesi algoritmo di rilevanza
          </h2>
          <p className="text-sm text-muted-foreground max-w-xl" data-id="element-1177">
            Configura come vengono calcolati i punteggi. Le modifiche verranno
            applicate ai nuovi articoli analizzati.
          </p>
        </div>
        <div className="flex gap-2.5 flex-shrink-0" data-id="element-1178">
          <Button variant="outline" onClick={() => setWeights(defaultWeights)} className="rounded-full border-accent text-accent hover:bg-accent/10 hover:text-accent h-11 px-6 text-sm" data-id="element-1179">
            Ripristina default
          </Button>
          <Button className="rounded-full h-11 px-6 text-sm" data-id="element-1180">Salva</Button>
        </div>
      </div>

      <div className="space-y-12" data-id="element-1181">
        {sections.map(section => <div key={section.title} data-id="element-1182">
            <div className="mb-4" data-id="element-1183">
              <h3 className="text-base font-semibold mb-1" data-id="element-1184">{section.title}</h3>
              {section.description && <p className="text-sm text-muted-foreground" data-id="element-1185">
                  {section.description}
                </p>}
            </div>
            <div className="divide-y divide-border border-y border-border" data-id="element-1186">
              {section.items.map(item => <WeightRow key={item.key} label={item.label} description={item.description} value={weights[item.key]} onChange={v => update(item.key, v)} max={item.max} data-id="element-1187" />)}
            </div>
          </div>)}
      </div>
    </div>;
}
export function Settings() {
  const location = useLocation();
  const isAlgorithm = location.pathname.includes('algorithm');
  return <div className="px-6 py-6 max-w-[1400px] mx-auto w-full" data-id="element-1188">
      <header className="mb-8" data-id="element-1189">
        <h1 className="text-3xl md:text-4xl font-semibold tracking-tight mb-2" data-id="element-1190">
          {isAlgorithm ? 'Algoritmo' : 'Connettori'}
        </h1>
        <p className="text-base text-muted-foreground" data-id="element-1191">
          {isAlgorithm ? "Configura i pesi e i parametri dell'algoritmo di rilevanza" : 'Gestisci i connettori per la raccolta automatica'}
        </p>
      </header>

      <div className="bg-card border border-border rounded-lg p-6" data-id="element-1192">
        {isAlgorithm ? <AlgorithmTab data-id="element-1193" /> : <ConnectorsTab data-id="element-1194" />}
      </div>
    </div>;
}